import React, { createContext, useContext, useEffect, useState } from 'react';
import { convexClient, hasLiveConvex } from '../lib/convexClient';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('convex_session_token');
      const savedUser = localStorage.getItem('convex_user');
      
      if (savedToken) {
        if (hasLiveConvex) {
          try {
            const userData = await convexClient.query("users:getUserByToken", { sessionToken: savedToken });
            if (userData) {
              setUser(userData);
              localStorage.setItem('convex_user', JSON.stringify(userData));
            } else {
              localStorage.removeItem('convex_session_token');
              localStorage.removeItem('convex_user');
              setUser(null);
            }
          } catch (err) {
            console.warn("Failed to check Convex token, using cached user:", err);
            if (savedUser) setUser(JSON.parse(savedUser));
          }
        } else {
          if (savedUser) setUser(JSON.parse(savedUser));
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const signUp = async ({ email, password, options }) => {
    const fullName = options?.data?.full_name || 'New User';
    const username = options?.data?.username || email.split('@')[0];

    const fallbackToMock = () => {
      const id = 'mock-' + Date.now();
      const mockUserData = { id, email, fullName, username };
      localStorage.setItem('convex_session_token', 'mock-token-' + id);
      localStorage.setItem('convex_user', JSON.stringify(mockUserData));
      
      const mockProfiles = JSON.parse(localStorage.getItem('mock_profiles') || '{}');
      mockProfiles[id] = {
        id, full_name: fullName, username,
        created_at: new Date().toISOString(),
        friends_count: 0, bio: 'Hello! I am new here.', location: 'Earth', avatar_url: null
      };
      localStorage.setItem('mock_profiles', JSON.stringify(mockProfiles));
      setUser(mockUserData);
      return { data: { user: mockUserData }, error: null };
    };

    if (hasLiveConvex) {
      try {
        const result = await convexClient.mutation("users:signUp", {
          email, password, fullName, username
        });
        
        localStorage.setItem('convex_session_token', result.sessionToken);
        const userData = { id: result.userId, email: result.email, fullName, username };
        localStorage.setItem('convex_user', JSON.stringify(userData));
        setUser(userData);
        return { data: { user: userData }, error: null };
      } catch (err) {
        console.warn("Convex backend unavailable, falling back to mock auth.", err);
        return fallbackToMock();
      }
    } else {
      return fallbackToMock();
    }
  };

  const signIn = async ({ email, password }) => {
    if (hasLiveConvex) {
      try {
        const result = await convexClient.mutation("users:signIn", { email, password });
        localStorage.setItem('convex_session_token', result.sessionToken);
        
        const userData = await convexClient.query("users:getUserByToken", { sessionToken: result.sessionToken });
        localStorage.setItem('convex_user', JSON.stringify(userData));
        setUser(userData);
        return { data: { user: userData, session: { user: userData } }, error: null };
      } catch (err) {
        return { data: null, error: err };
      }
    } else {
      const id = 'mock-123';
      const mockUserData = {
        id,
        email,
        fullName: 'Alex Johnson',
        username: 'alexj',
        avatarUrl: null
      };
      localStorage.setItem('convex_session_token', 'mock-token-' + id);
      localStorage.setItem('convex_user', JSON.stringify(mockUserData));
      setUser(mockUserData);
      return { data: { user: mockUserData, session: { user: mockUserData } }, error: null };
    }
  };

  const signOut = async () => {
    const token = localStorage.getItem('convex_session_token');
    if (hasLiveConvex && token) {
      try {
        await convexClient.mutation("users:signOut", { sessionToken: token });
      } catch (err) {
        console.warn("Failed to sign out from Convex server:", err);
      }
    }
    localStorage.removeItem('convex_session_token');
    localStorage.removeItem('convex_user');
    setUser(null);
    return { error: null };
  };

  const updateUser = (updates) => {
    setUser(prev => {
      if (!prev) return null;
      const updated = {
        ...prev,
        fullName: updates.fullName !== undefined ? updates.fullName : prev.fullName,
        username: updates.username !== undefined ? updates.username : prev.username,
        avatarUrl: updates.avatarUrl !== undefined ? updates.avatarUrl : prev.avatarUrl,
      };
      localStorage.setItem('convex_user', JSON.stringify(updated));
      return updated;
    });
  };

  const value = {
    signUp,
    signIn,
    signOut,
    updateUser,
    user: user ? {
      ...user,
      user_metadata: {
        full_name: user.fullName || user.email,
        username: user.username || user.email?.split('@')[0],
        avatar_url: user.avatarUrl || null,
      }
    } : null,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
